import { type FC, useState, useEffect, useMemo, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import Prompt from './Prompt';
import ModelSelector from './ModelSelector';
import VideoProperties from './VideoProperties';
import CoreUpsell from './CoreUpsell';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import {
  getVideoCreatorErrorCode,
  getVideoCreatorPricePoints,
  getVideoCreatorPrompt,
  getVideoCreatorStatus,
  getVideoCreatorIsCalculatingCost,
  getVideoCreatorRestrictions,
  getVideoCreatorModel,
  getVideoCreatorModelsExpanded,
} from '../../../selectors/studio/videoCreator';
import { VideoCreatorErrorCode } from '../../../types/store';
import { getModelPropertiesByType } from '../ModelProperties';
import {
  generateVideo,
  updateVideoCreatorErrorCode,
} from '../../../actions/studio/videoCreator';
import VideoCreatorPointsModal from '../../../modals/VideoCreatorPointsModal';
import VideoLegalModal from '../../../modals/VideoLegalModal';
import { Url } from '@wix/da-url';
import { useFirstTimeFlag } from '@wix/da-shared-react/pkg/redux/firstTimeFlags/hooks';
import { FirstTimeFlag } from '@wix/da-shared-react/pkg/redux/firstTimeFlags';
import { getFirstTimeFlags } from '@wix/da-shared-react/pkg/redux/firstTimeFlags/selectors';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import {
  BiData,
  BuyCoreButtonBiEvent,
  VideoCreatorCreateButtonClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { VIDEO_CREATOR_LAYOUT } from './VideoCreatorControls.constants';

import s from './VideoCreatorControls.scss';

const LONG_PROMPT_LENGTH = 50;

export interface Props {}

const VideoCreatorControls: FC<Props> = () => {
  const { t } = useTranslation();
  const logBiEvent = useBiLogger();

  const isMobile = useContext(MobileContext);
  const { hasCoreMembership } = useContext(SessionContext);
  const restrictions = useSelector(getVideoCreatorRestrictions);
  const dispatch = useDispatch();
  const pricePoints = useSelector(getVideoCreatorPricePoints);
  const prompt = useSelector(getVideoCreatorPrompt);
  const errorCode = useSelector(getVideoCreatorErrorCode);
  const isBusy = useSelector(getVideoCreatorStatus) === 'busy';
  const selectedModel = useSelector(getVideoCreatorModel);
  const isCalculatingCost = useSelector(getVideoCreatorIsCalculatingCost);
  const firstTimeFlags = useSelector(getFirstTimeFlags);
  const isModelsExpanded = useSelector(getVideoCreatorModelsExpanded);
  const isOutOfPrompts = useMemo(() => {
    if (!hasCoreMembership) {
      return restrictions?.videoPromptsRemaining < 1;
    } else {
      return false;
    }
  }, [hasCoreMembership, restrictions?.videoPromptsRemaining]);

  const { dropFlag } = useFirstTimeFlag(
    FirstTimeFlag.IsVideoCreatorModalNotSeen
  );
  const hasError =
    errorCode !== VideoCreatorErrorCode.NO_ERROR &&
    errorCode !== VideoCreatorErrorCode.INSUFFICIENT_POINTS_BALANCE;
  const [isPointsModalOpen, setIsPointsModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const hasLongPrompt = prompt.length > LONG_PROMPT_LENGTH;

  const availableModels = useMemo(
    () => restrictions?.videoModelsAvailable ?? [],
    [restrictions]
  );
  const model = useMemo(() => {
    return getModelPropertiesByType(selectedModel);
  }, [selectedModel]);
  const showAudio = model?.hasSound ?? false;

  const isModelAvailable = useMemo(() => {
    const index = availableModels.findIndex(id => id === model?.id);
    return index >= 0;
  }, [availableModels, model?.id]);

  const buttonVariant =
    isModelAvailable && !isOutOfPrompts ? 'brand-green' : 'core-orange';

  const canGenerate = useMemo(() => {
    if (isBusy || !pricePoints || prompt.length === 0) {
      return false;
    }
    return true;
  }, [pricePoints, prompt, isBusy]);

  const pointsBalance = restrictions?.pointsBalance ?? 0;
  const hasEnoughPoints = pricePoints > 0 && pointsBalance >= pricePoints;

  // Undefined or false means they've seen the modal, so don't show it
  const showFirstGenModal =
    firstTimeFlags[FirstTimeFlag.IsVideoCreatorModalNotSeen] === true;

  useEffect(() => {
    if (errorCode === VideoCreatorErrorCode.INSUFFICIENT_POINTS_BALANCE) {
      setIsPointsModalOpen(true);
      dispatch(updateVideoCreatorErrorCode(VideoCreatorErrorCode.NO_ERROR));
    }
  }, [errorCode, dispatch]);

  const templateRows = useMemo(() => {
    const {
      promptShortNoError,
      promptErrorDelta,
      promptLongDelta,
      promptMobileDelta,
      propertiesHeightNoAudio,
      propertiesAudioDelta,
      modelAreaHeight,
      modelAreaHeightExpanded,
      modelAreaHeightMobile,
    } = VIDEO_CREATOR_LAYOUT;

    let promptHeight = promptShortNoError;
    if (hasError) {
      promptHeight += promptErrorDelta;
    }
    if (hasLongPrompt) {
      promptHeight += promptLongDelta;
    }
    if (isMobile) {
      promptHeight -= promptMobileDelta;
    }

    let propertiesHeight = propertiesHeightNoAudio;
    if (showAudio) {
      propertiesHeight += propertiesAudioDelta;
    }
    const modelHeight = isMobile
      ? modelAreaHeightMobile
      : isModelsExpanded
        ? modelAreaHeightExpanded
        : modelAreaHeight;

    return `${promptHeight}px ${modelHeight}px ${propertiesHeight}px 1fr`;
  }, [hasError, hasLongPrompt, showAudio, isMobile, isModelsExpanded]);

  const handleGenerateClick = () => {
    // We don't want to allow clicks while the displayed price could be wrong, but disabling
    // the button makes the button flash and looks like a glitch. Everything usually happens so fast,
    // but this is a failsafe in case the generation_cost endpoint takes longer than normal.
    if (isCalculatingCost) {
      return;
    }
    if (!isModelAvailable || isOutOfPrompts) {
      logBiEvent(
        BiData<BuyCoreButtonBiEvent>({
          evid: 375,
          referral_info: ReferralType.VIDEO_CREATOR_CREATE_BUTTON,
        })
      );
      window.open(
        Url.coreMembershipLink({ referer: Url.videoCreatorLink() }),
        '_blank'
      );
      return;
    }
    if (!hasEnoughPoints) {
      logBiEvent(
        BiData<VideoCreatorCreateButtonClickBiEvent>({
          evid: 950,
          prompt: prompt,
          ai_model: model?.id ?? '',
          is_valid: false,
        })
      );
      setIsPointsModalOpen(true);
      return;
    }
    if (!showFirstGenModal) {
      dispatch(generateVideo());
    } else {
      setIsConfirmationModalOpen(true);
    }
  };

  const handleConfirmationConfirm = () => {
    dropFlag();
    setIsConfirmationModalOpen(false);
    dispatch(generateVideo());
  };

  const handleConfirmationClose = () => {
    dropFlag();
    setIsConfirmationModalOpen(false);
  };

  return (
    <div
      className={cn(
        s['root'],
        hasError && s['has-error'],
        isMobile && s['mobile'],
        !isMobile && s['desktop']
      )}
    >
      <div
        className={cn(
          s['scroller'],
          showAudio && s['has-audio'],
          hasLongPrompt && s['long-prompt']
        )}
        style={{ gridTemplateRows: templateRows }}
      >
        <Prompt />
        <ModelSelector />
        <VideoProperties />
        {!isMobile && <CoreUpsell />}
      </div>
      <div className={s['create-area']}>
        <BrandedButton
          className={cn(
            s['create-button'],
            isCalculatingCost && s['is-calculating']
          )}
          key="submit"
          variant={buttonVariant}
          size="large"
          disabled={!canGenerate}
          onClick={handleGenerateClick}
        >
          {!pricePoints && (
            <div className={s['button-calculating']}>
              <div className={s['spinner-anchor']}>
                <div className={s['button-spinner']} />
              </div>
              <span className={s['spinner-label']}>
                {t('studio.videoCreator.creatButton.calculating')}
              </span>
            </div>
          )}
          {pricePoints &&
            t('studio.videoCreator.createButton', {
              postProcess: 'reactPostprocessor',
              points: pricePoints,
            })}
        </BrandedButton>
      </div>
      {isMobile && <CoreUpsell />}
      <VideoLegalModal
        isOpen={isConfirmationModalOpen}
        onConfirm={handleConfirmationConfirm}
        onRequestClose={handleConfirmationClose}
      />
      <VideoCreatorPointsModal
        isOpen={isPointsModalOpen}
        onRequestClose={() => setIsPointsModalOpen(false)}
      />
    </div>
  );
};

VideoCreatorControls.displayName = 'VideoCreatorControls';
export default VideoCreatorControls;
